import {
    EnumListItem,
    MeasurementListItem,
    MenuPreference,
    NfcStickerProductItemModel,
    PhysicalProductClassificationCategory,
    PhysicalProductItemType,
    PhysicalProductLink,
    PhysicalProductLinkListItem,
    PhysicalProductVariationListItem,
    ProductExtraItemModel
} from '../../core';
import { IVariationAttachment } from '../../model/open360BusinessLogicRetailersPhysicalProductsBEPhysicalProductVariation';
import { IProductIngredient } from '../../model/products/IProductIngredient';
import { IProductVariationIngredient } from '../../model/products/IProductVariationIngredient';

export interface IPhysicalProductVariationListItem extends PhysicalProductVariationListItem {
    isOpenEditPrice: boolean;
    isOpenEditDiscount: boolean;
    isOpenEditStockLevel: boolean;
    price: number | string;
    oldPrice: number | string;
    insidePrice: number | string;
    insideVAT: number | string;
    vat: number | string;
    discount: number | string;
    isFoodType: boolean;
    error: {
        noPrice: boolean;
        oldPriceIsSmall: boolean;
        takeawayGreatEatIn: boolean;
    };
}

export interface IPhysicalProduct {
    title: string;
    brands: any[];
    category: PhysicalProductClassificationCategory;
    description: string;
    shortDescription: string;
    nfcStickers?: NfcStickerProductItemModel[];
    verificationRequired: boolean;
    isInformation: boolean;
    links: IPhysicalProductLink[];
    itemTypeID: number;
    itemType: PhysicalProductItemType;
    itemTypes: PhysicalProductItemType[];
    gender: number;
    genderItem: EnumListItem;
    genderItems: EnumListItem[];
    measurementTypes: IMeasurementType[];
    measurements: MeasurementListItem[];
    variations: IPhysicalProductVariation[];
    error: {
        summary: string;
        title: string;
        category: string;
        brand: string;
        description: string;
        shortDescription: string;
        variations: string;
        stickers: INfcStickersErrors[];
        preparation: string;
        ingredients: string;
        ingredientList: string;
        itemType: string;
        originCountry: string;
    };
    draft: boolean;
    measurementItems: {};
    measurementTypeEnable: {};
    measurementTypeVisible: {};
    measurementEnable: boolean;
    sizeName: string;
    colourName: string;
    foodPreferences: IFoodPreference[];
    allergens: IFoodPreference[];
    extras: IProductExtraItemModel[];
    ingredientList: IProductIngredient[];
    originCountry: EnumListItem;
    preparation: string;
    ingredients: string;
    preOrder: boolean;
}

export interface INfcStickersErrors {
    id: number;
    nfcNumber: string;
    store: string;
}

export interface IPhysicalProductVariation {
    id: string;
    color: string;
    size: string;
    sku: string;
    badges: IBadge[];
    price: number | string;
    oldPrice: number | string;
    vat: number | string;
    insidePrice: number | string;
    insideVAT: number | string;
    measurementItemValues: {};
    measurementTypeItem: {};
    imagesID: string[];
    ingredients?: IProductVariationIngredient[];
    imageID: string;
    discount?: number;
    isHot?: boolean;
    preparationTime?: number;
    specialOffer: ISpecialOffer;
    stockQuantity?: number | string;
    attachment: IVariationAttachment;
    isAccessibleOutOfSession: boolean;

    error: {
        sizes?: {};
        sku?: string;
        color?: string;
        size?: string;
        price?: string;
        oldPrice?: string;
        storeVariations?: string;
        image?: string;
        variations?: string;
        takeawayGreatEatIn?: boolean;
        preparationTime?: boolean;
        ingredient?: boolean;
    };
}

export interface IVariationDiscount {
    ID: string;
    Discount: number;
}

export interface IPhysicalProductLink extends PhysicalProductLink {
    type: ILinkListItem;
    error: {
        sum?: string;
        title?: string;
        url?: string;
        type?: string;
    };
}

export interface IPhysicalProductLinkListItem extends PhysicalProductLinkListItem {
    viewClass: string;
}

export interface ILinkListItem extends EnumListItem {
    viewClass: string;
}

export interface IHttpResponseCustom<T> {
    data: T;
    status: number;
    headers: (headerName?: string) => string | { [key: string]: string };
    config: any;
    statusText: string;
}

export interface IBadge {
    key: number;
    name: string;
    selected: boolean;
    order: number;
    color: string;
    textcolor: string;
}

export interface IMeasurementType {
    ID: number;
    Name: string;
    SelectText: string;
}

export interface IMeasurementListItem extends MeasurementListItem {
    Value: number;
}

export interface IColumnProductSortSet {
    column: string;
    order: string;
    title: string;
    classSort: string;
}

export interface IProductSearchItem {
    ID: string;
    Title: string;
    ImageID: string;
    MinPrice: number;
    MaxPrice: number;
    OldPrice?: number;
    ImageUrl?: string;
    CurrencySign?: string;
    ClassificationCategoryID?: number;
    ItemTypeID: number;
    ItemTypeName: string;
    Variants: IProductSearchItemVariant[];
}

export interface IProductSearchItemVariant {
    ID: string;
    Price: number;
    OldPrice: number;
    Size: string;
    Colour: string;
    IsArchived: boolean;
}

export interface IProductSearchPagingResult {
    Items: IProductSearchItem[];
    Total: number;
}

export interface IFoodPreference extends MenuPreference {
    IsSelected: boolean;
}

export interface IProductExtraItemModel extends ProductExtraItemModel {
    QuantityError: string;
}

export interface ISpecialOffer {
    OfferValidFrom: Date;
    OfferValidTo: Date;
    Type: SpecialOfferTypeEnum;
    TemplateType: OfferTemplateTypeEnum;
    BuyXGetYOfferValues: {
        Get?: number,
        Buy?: number
    };
    BuyXForYSumYOfferValues : {
        Buy?: number,
        Sum?: number | string
    };
    DiscountPrice: number | string;
    error: {
        startDate?: string;
        endDate?: string;
        price?: string;
        buyQuantity?: string;
        getQuantity?: string;
    };
}

export enum SpecialOfferTypeEnum {
    None = 0,
    BuyXGetY = 1,
    Discount = 2,
    BuyXForY = 3
}

export interface ISpecialOfferChangeMessage {
    variationID: string;
    badgeValue: boolean;
}

export enum OfferTemplateTypeEnum {
    None,
    SaveXX,
    SaveXXp,
    NowOnlyXX,
    NowOnlyXXp,
    OnlyXX,
    BuyXForY,
    BuyXForYp
}

export interface IOfferType {
    name: string;
    value: number;
    validators: Array<(offer: ISpecialOffer, price: number | string) => boolean>;
    templates?: IOfferTemplate[];
}

export interface IOfferTemplate {
    name: string;
    value: number;
}

export interface IVariationStockLevel {
    ID: string;
    StockQuantity: number;
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/products/models/index.ts